import { Ionicons } from '@expo/vector-icons';
import { router, type Href } from 'expo-router';
import React, { useMemo } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';

import { AccountFlagBadges } from './account-flag-badges';
import { accountLabel, type CronPreviewRow } from './post-cron-shared';

import { Portal } from '@/components/ui/portal';
import { useLedger } from '@/hooks/use-accounting';
import type { LedgerEntry } from '@/types/models';
import { formatBusinessDate } from '@/utils/date-utils';
import { formatMoneyCents } from '@/utils/format-money';

// Primary line for a ledger entry — the GL code (number + name) for a charge,
// otherwise the entry kind. This is the per-line GL breakdown the PM needs:
// dues fan out into one charge per GL code, so each shows its own account.
const activityTitle = (e: LedgerEntry): string => {
	if (e.kind === 'charge') {
		if (e.glCodeName) return e.glCodeNumber ? `${e.glCodeNumber} · ${e.glCodeName}` : e.glCodeName;
		return e.category ?? 'Charge';
	}
	if (e.kind === 'payment') return e.method ? `Payment · ${e.method}` : 'Payment';
	return e.kind.charAt(0).toUpperCase() + e.kind.slice(1);
};

// Secondary line — date plus the invoice it belongs to (when grouped).
const activitySub = (e: LedgerEntry): string => {
	const parts = [formatBusinessDate(e.occurredOn)];
	if (e.invoiceNumber) parts.push(`Invoice #${e.invoiceNumber}`);
	else if (e.reference) parts.push(e.reference);
	return parts.join(' · ');
};

/**
 * Right-side detail drawer for the Post Dues / Post Late Fees pages. Lets a PM
 * inspect a customer — current balance, what's overdue (split by GL/category),
 * last payment, and recent ledger activity — WITHOUT leaving the posting page
 * (so the date/period and the row selection are preserved). Opening "Open
 * account" launches the full account page in a new tab on web.
 */
export const AccountDetailDrawer: React.FC<{ row: CronPreviewRow | null; onClose: () => void }> = ({ row, onClose }) => {
	const { width } = useWindowDimensions();
	const { data: ledgerResp, isLoading } = useLedger(row?.accountId);
	const entries = useMemo(() => ledgerResp?.data ?? [], [ledgerResp]);

	// useLedger is newest-first; the first non-voided payment is the last one made.
	const lastPayment = useMemo(
		() => entries.find((e) => e.kind === 'payment' && !e.voidedAt),
		[entries],
	);
	const recent = useMemo(() => entries.slice(0, 8), [entries]);

	if (!row) return null;
	const panelWidth = Math.min(420, Math.max(300, width * 0.9));

	// Open a detail surface without losing the posting page: a new tab on web,
	// a push (closing the drawer) on native.
	const openHref = (href: string) => {
		if (Platform.OS === 'web' && typeof window !== 'undefined') {
			window.open(href, '_blank', 'noopener');
		} else {
			onClose();
			router.push(href as Href);
		}
	};
	const openAccount = () => openHref(`/accounting/account/${row.accountId}`);
	// A ledger entry's "full bill" is its invoice when it has one; otherwise the
	// account ledger deep-linked (highlighted) to that exact charge row — imported
	// bills have no invoice record, their detail lives on the charge itself.
	const billHref = (e: LedgerEntry): string =>
		e.invoiceId
			? `/accounting/invoice/${e.invoiceId}`
			: `/accounting/account/${row.accountId}?highlight=${e.id}`;
	// An overdue bucket may span several charges/invoices; open the oldest bill —
	// the invoice when there is one, else the oldest contributing charge row.
	const openBucketBill = (b: { invoiceIds?: string[]; chargeIds?: string[] }) => {
		const href = b.invoiceIds && b.invoiceIds.length > 0
			? `/accounting/invoice/${b.invoiceIds[0]}`
			: b.chargeIds && b.chargeIds.length > 0
				? `/accounting/account/${row.accountId}?highlight=${b.chargeIds[0]}`
				: `/accounting/account/${row.accountId}`;
		openHref(href);
	};

	return (
		<Portal>
			<View style={styles.overlay} pointerEvents="box-none">
				<Pressable style={styles.backdrop} onPress={onClose} />
				<View style={[styles.panel, { width: panelWidth }]}>
					<View style={styles.header}>
						<View style={{ flex: 1 }}>
							<Text style={styles.title} numberOfLines={2}>{accountLabel(row)}</Text>
							{row.flags && row.flags.length > 0 && (
								<View style={styles.flags}><AccountFlagBadges flags={row.flags} size="compact" /></View>
							)}
						</View>
						<Pressable onPress={onClose} hitSlop={10}><Ionicons name="close" size={22} color="#607d8b" /></Pressable>
					</View>

					<ScrollView contentContainerStyle={styles.body}>
						{/* The dollars this run will actually charge, next to the balance —
						    the drawer is opened precisely to answer "and what is this one
						    getting billed?" */}
						{row.dueCents > 0 && (
							<View style={styles.statRow}>
								<Text style={styles.statLabel}>Dues this period</Text>
								<Text style={[styles.statValue, styles.dues]}>{formatMoneyCents(row.dueCents)}</Text>
							</View>
						)}
						<View style={styles.statRow}>
							<Text style={styles.statLabel}>Current balance</Text>
							<Text style={[styles.statValue, row.currentBalanceCents > 0 && styles.owed]}>
								{formatMoneyCents(row.currentBalanceCents)}
							</Text>
						</View>
						<View style={styles.statRow}>
							<Text style={styles.statLabel}>Last payment</Text>
							<Text style={styles.statValue}>
								{lastPayment
									? `${formatMoneyCents(Math.abs(lastPayment.signedAmountCents))} · ${formatBusinessDate(lastPayment.occurredOn)}`
									: '—'}
							</Text>
						</View>

						{row.overdue && row.overdue.length > 0 && (
							<View style={styles.section}>
								<Text style={styles.sectionTitle}>Overdue</Text>
								{row.overdue.map((b) => {
									const n = b.chargeIds?.length ?? 0;
									return (
										<Pressable
											key={b.bucket}
											style={styles.lineRow}
											onPress={() => openBucketBill(b)}
											testID={`overdue-bucket-${b.bucket}`}
										>
											<Text style={[styles.lineLabel, styles.linkish, !b.triggers && styles.exempt]} numberOfLines={1}>
												{b.bucket}{!b.triggers ? '  (no late fee)' : ''}{n > 1 ? `  (${n} bills)` : ''}
											</Text>
											<Text style={[styles.lineAmt, !b.triggers && styles.exempt]}>{formatMoneyCents(b.cents)}</Text>
											<Ionicons name="chevron-forward" size={13} color="#b0bec5" />
										</Pressable>
									);
								})}
							</View>
						)}

						<View style={styles.section}>
							<Text style={styles.sectionTitle}>Recent activity</Text>
							{isLoading ? (
								<ActivityIndicator style={{ marginTop: 8 }} />
							) : recent.length === 0 ? (
								<Text style={styles.muted}>No ledger activity.</Text>
							) : recent.map((e) => (
								<Pressable
									key={`${e.kind}-${e.id}`}
									style={styles.activityRow}
									onPress={() => openHref(billHref(e))}
									testID={`activity-${e.kind}-${e.id}`}
								>
									<View style={styles.activityMain}>
										<Text style={[styles.lineLabel, styles.linkish]} numberOfLines={1}>{activityTitle(e)}</Text>
										{activitySub(e) ? <Text style={styles.activitySub} numberOfLines={1}>{activitySub(e)}</Text> : null}
									</View>
									<Text style={[styles.lineAmt, e.signedAmountCents < 0 ? styles.credit : undefined]}>
										{formatMoneyCents(e.signedAmountCents)}
									</Text>
									<Ionicons name="chevron-forward" size={13} color="#b0bec5" />
								</Pressable>
							))}
						</View>
					</ScrollView>

					<Pressable style={styles.openBtn} onPress={openAccount}>
						<Ionicons name="open-outline" size={16} color="#1976d2" />
						<Text style={styles.openLabel}>Open account{Platform.OS === 'web' ? ' ↗' : ''}</Text>
					</Pressable>
				</View>
			</View>
		</Portal>
	);
};

const styles = StyleSheet.create({
	overlay: { ...StyleSheet.absoluteFill, position: Platform.OS === 'web' ? ('fixed' as 'absolute') : 'absolute', flexDirection: 'row', justifyContent: 'flex-end', zIndex: 1000 },
	backdrop: { ...StyleSheet.absoluteFill, backgroundColor: 'rgba(0,0,0,0.28)' },
	panel: { backgroundColor: '#fff', height: '100%', borderLeftWidth: 1, borderLeftColor: '#cfd8dc', shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 16, shadowOffset: { width: -4, height: 0 } },
	header: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, padding: 16, borderBottomWidth: 1, borderBottomColor: '#eceff1' },
	title: { fontSize: 16, fontWeight: '700', color: '#212121' },
	flags: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 6 },
	body: { padding: 16, gap: 4 },
	statRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
	statLabel: { fontSize: 13, color: '#607d8b' },
	statValue: { fontSize: 14, fontWeight: '600', color: '#263238' },
	owed: { color: '#c62828' },
	dues: { color: '#0d47a1' },
	section: { marginTop: 14 },
	sectionTitle: { fontSize: 11, fontWeight: '700', color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
	lineRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 5, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#eceff1' },
	activityRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, paddingVertical: 6, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#eceff1' },
	activityMain: { flex: 1, minWidth: 0 },
	activitySub: { fontSize: 11, color: '#90a4ae', marginTop: 1 },
	lineLabel: { fontSize: 13, color: '#37474f', flex: 1, minWidth: 0 },
	linkish: { color: '#1565c0' },
	lineAmt: { fontSize: 13, color: '#263238', fontVariant: ['tabular-nums'] },
	credit: { color: '#2e7d32' },
	exempt: { color: '#9e9e9e', fontStyle: 'italic' },
	muted: { color: '#9e9e9e', fontStyle: 'italic', fontSize: 13 },
	openBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 14, borderTopWidth: 1, borderTopColor: '#eceff1' },
	openLabel: { color: '#1976d2', fontWeight: '600', fontSize: 14 },
});
